import { GBFDecoder } from './gbf/gbf';
import { MAX_AUTO_REDOWNLOADS } from './constants';

/**
 * Pure helpers for the per-session download manifest (stored in IndexedDB `session-manifest`).
 * No IndexedDB / fetch here: everything is testable in isolation.
 */

export interface DoneEntry {
  bytes: number;
  records: number;
  firstTime: number | null;
  lastTime: number | null;
}

export interface FailedEntry {
  attempts: number;
  error: string;
  at: number;
}

export interface SessionManifest {
  sessionId: string;
  expected: string[];
  done: Record<string, DoneEntry>;
  failed: Record<string, FailedEntry>;
  autoRedownloads: number;
  updatedAt: number;
}

export interface GbfValidation {
  ok: boolean;
  records: number;
  firstTime: number | null;
  lastTime: number | null;
  error: string | null;
}

export const createManifest = (
  sessionId: string,
  expected: string[],
  autoRedownloads = 0,
): SessionManifest => ({
  sessionId,
  expected: [...expected],
  done: {},
  failed: {},
  autoRedownloads,
  updatedAt: Date.now(),
});

/** An entry is broken when it describes an empty / undecodable block. */
export const isBroken = (entry?: DoneEntry | null): boolean => !entry || !(entry.bytes > 0) || !(entry.records > 0);

const toSet = (ids: Array<string | number>) => new Set(ids.map(String));

/** Expected athlete ids that are not usable yet (no valid done entry, no stored block, or failed). */
export const missingIds = (
  manifest: SessionManifest | null | undefined,
  storedIds: Array<string | number>,
): string[] => {
  if (!manifest) return [];
  const stored = toSet(storedIds);

  return manifest.expected.map(String).filter((id) => (
    !manifest.done[id]
    || isBroken(manifest.done[id])
    || !stored.has(id)
    || !!manifest.failed[id]
  ));
};

/** True only if a manifest exists, lists at least one athlete and every one of them is downloaded and valid. */
export const isComplete = (
  manifest: SessionManifest | null | undefined,
  storedIds: Array<string | number>,
): boolean => !!manifest && manifest.expected.length > 0 && missingIds(manifest, storedIds).length === 0;

/**
 * Validates a downloaded GBF buffer:
 * - byteLength equals Content-Length when the header was present
 * - header parses
 * - at least one record and no truncated trailing record
 */
export const validateGbfBuffer = (
  buffer: ArrayBuffer,
  contentLength?: number | null,
): GbfValidation => {
  const fail = (error: string, records = 0): GbfValidation => ({
    ok: false, records, firstTime: null, lastTime: null, error,
  });

  if (!buffer || buffer.byteLength === 0) {
    return fail('empty buffer');
  }

  if (contentLength != null && Number.isFinite(contentLength) && contentLength !== buffer.byteLength) {
    return fail(`size mismatch: got ${buffer.byteLength} bytes, expected ${contentLength}`);
  }

  try {
    const info = new GBFDecoder(buffer).inspect();

    if (info.recordSize <= 0) return fail('invalid record size');
    if (info.recordCount <= 0) return fail('no records');
    if (info.remainder !== 0) {
      return fail(`truncated record (${info.remainder} stray bytes)`, info.recordCount);
    }

    return {
      ok: true,
      records: info.recordCount,
      firstTime: info.firstTime,
      lastTime: info.lastTime,
      error: null,
    };
  } catch (e) {
    return fail(`undecodable: ${e instanceof Error ? e.message : String(e)}`);
  }
};

export const entryFromValidation = (bytes: number, v: GbfValidation): DoneEntry => ({
  bytes,
  records: v.records,
  firstTime: v.firstTime,
  lastTime: v.lastTime,
});

/**
 * Stored blocks that have no usable manifest entry (blocks written before the manifest existed):
 * the caller must read and validate them to know whether to keep them.
 */
export const idsNeedingValidation = (
  expected: string[],
  manifest: SessionManifest | null | undefined,
  storedIds: Array<string | number>,
): string[] => {
  const stored = toSet(storedIds);
  return expected.map(String).filter((id) => stored.has(id) && !manifest?.done[id]);
};

export interface SyncPlan {
  /** entries that can be reused as-is */
  done: Record<string, DoneEntry>;
  /** ids to download */
  toDownload: string[];
  /** stored blocks to delete before downloading */
  toDeleteBlocks: string[];
  /** every block of the session is dropped and re-downloaded (counted in autoRedownloads) */
  invalidateAll: boolean;
  autoRedownloads: number;
}

/**
 * Decides what to keep / download.
 * Only empty or undecodable blocks (no records) are "broken": when any exists, the whole session is dropped and
 * re-downloaded, at most MAX_AUTO_REDOWNLOADS times per session; past the cap only the broken blocks are retried.
 * Length differences between athletes are NOT a signal (substitutes, late device start are legit).
 */
export const planSync = (
  expected: string[],
  manifest: SessionManifest | null | undefined,
  storedIds: Array<string | number>,
  legacyValidations: Record<string, GbfValidation> = {},
  bytesOf: Record<string, number> = {},
): SyncPlan => {
  const stored = toSet(storedIds);
  const done: Record<string, DoneEntry> = {};
  const toDownload: string[] = [];
  const broken: string[] = [];
  const autoRedownloads = manifest?.autoRedownloads ?? 0;

  for (const rawId of expected) {
    const id = String(rawId);

    if (!stored.has(id)) {
      toDownload.push(id);
      continue;
    }

    const entry = manifest?.done[id];

    if (entry) {
      if (isBroken(entry)) broken.push(id); else done[id] = entry;
    } else {
      const v = legacyValidations[id];
      if (v?.ok) {
        done[id] = entryFromValidation(bytesOf[id] ?? 0, v);
      } else {
        broken.push(id);
      }
    }
  }

  if (broken.length > 0 && autoRedownloads < MAX_AUTO_REDOWNLOADS) {
    return {
      done: {},
      toDownload: expected.map(String),
      toDeleteBlocks: [...stored],
      invalidateAll: true,
      autoRedownloads: autoRedownloads + 1,
    };
  }

  return {
    done,
    toDownload: [...toDownload, ...broken],
    toDeleteBlocks: broken,
    invalidateAll: false,
    autoRedownloads,
  };
};
